// engine2025/blocks/economicActivity.ts — F4: Actividades económicas (§A.5).
//
// Fuentes: docs/guia-renta-2025/_schema.md §A.5;
//   Cap.06 (06-actividades-economicas-cuestiones-generales.md) — afectación/desafectación (T4.1)
//   Cap.07 (07-metodo-estimacion-directa.md) — ED simplificada (T4.2)
//   Cap.08 (08-estimacion-objetiva-no-agricola.md) — EO no agrícola, módulos (T4.3)
//   Cap.09 (09-actividades-economicas-estimacion-objetiva-agricola.md) — EO agrícola (T4.4)
// Golden: caso-06 (afectación local estomatólogo), caso-07 (EDS radiólogo), caso-08 (bar 673.1),
// caso-09 (finca agrícola-ganadera).
//
// Alcance (regla dura del plan, riesgo de F4): los módulos de EO son tablas por epígrafe/código
// de producto — solo se implementan los epígrafes/códigos que ejercitan los goldens (673.1 para
// EO no agrícola; códigos 3/6/15 para EO agrícola). Cualquier otro epígrafe/código lanza un error
// explícito en vez de aproximar.

import type { Cents } from '../money';
import { toCents, subtractCents, sumCents, minCents, roundHalfUp } from '../money';
import type { ParamRegistry } from '../params/registry';

// ============================================================================================
// T4.1 — Afectación y desafectación de elementos patrimoniales (Cap.06 §3.2)
// ============================================================================================

export interface HistoricalDepreciationPeriod {
    years: number;
    months: number;
    ratePerYear: number; // %
}

/** Amortizaciones fiscalmente deducibles acumuladas sobre una base fija, por tramos de coeficiente histórico. */
export function computeAccumulatedDepreciation(base: Cents, periods: HistoricalDepreciationPeriod[]): Cents {
    const total = periods.reduce((acc, p) => acc + base * (p.ratePerYear / 100) * (p.years + p.months / 12), 0);
    return roundHalfUp(total);
}

/**
 * Art. 29 LIRPF / art. 22 RIRPF (Cap.06 §3.2) — valor de afectación de un elemento que pasa del
 * patrimonio personal al empresarial: coste de adquisición − amortizaciones ya deducidas.
 * caso-06: 65.000 − 34.545 = 30.455 €.
 */
export function computeAffectationValue(acquisitionValue: Cents, accumulatedDepreciation: Cents): Cents {
    return subtractCents(acquisitionValue, accumulatedDepreciation);
}

/**
 * Cap.06 §3.2 — desafectación: valor neto contable = valor de afectación − amortización del
 * período en que el elemento estuvo afecto. caso-06: base amortizable tras afectación (excluido
 * suelo) 12.455 €, 3% × 5/12 = 155,69 €; valor neto contable = 30.455 − 155,69 = 30.299,31 €.
 */
export function computeDisaffectationValue(
    affectationValue: Cents,
    landValue: Cents,
    ratePct: number,
    monthsAffected: number
): { periodAmortization: Cents; netBookValue: Cents } {
    const amortizableBase = subtractCents(affectationValue, landValue);
    const periodAmortization = roundHalfUp(amortizableBase * (ratePct / 100) * (monthsAffected / 12));
    return { periodAmortization, netBookValue: subtractCents(affectationValue, periodAmortization) };
}

// ============================================================================================
// T4.2 — Estimación directa simplificada (Cap.07 §3.2)
// ============================================================================================

export interface DirectEstimationSimplifiedInput {
    /** Ingresos íntegros fiscales: ventas/servicios + autoconsumo + variación existencias + subvenciones. */
    grossIncome: Cents;
    /** Gastos fiscales deducibles, ANTES de gastos de difícil justificación. */
    deductibleExpenses: Cents;
    /** Fase 2ª — no ejercitada por caso-07; si se marca, se aplica sobre el RN de Fase 1ª. */
    irregularReductionAmount?: Cents;
}

export interface DirectEstimationSimplifiedResult {
    difference: Cents;
    difficultJustificationExpense: Cents;
    netYieldPhase1: Cents;
    netYieldReduced: Cents;
    netYieldReducedTotal: Cents;
}

interface DifficultJustificationParam {
    pct: number;
    maxAmount: Cents;
}

/**
 * Art. 30.2.4ª LIRPF (P-NEW-10-1 / P-064-P-065) — gastos de difícil justificación en EDS: 5% de
 * la diferencia positiva (ingresos−gastos), tope 2.000 €/contribuyente. caso-07: 5%×60.100=3.005€
 * → tope 2.000 €.
 */
export function computeDirectEstimationSimplified(
    input: DirectEstimationSimplifiedInput,
    params: ParamRegistry
): DirectEstimationSimplifiedResult {
    const difference = subtractCents(input.grossIncome, input.deductibleExpenses);
    const { pct, maxAmount } = params.get<DifficultJustificationParam>('P-NEW-10-1');
    const difficultJustificationExpense = difference > 0 ? minCents(roundHalfUp(difference * (pct / 100)), maxAmount) : toCents(0);
    const netYieldPhase1 = subtractCents(difference, difficultJustificationExpense);

    const netYieldReduced = input.irregularReductionAmount
        ? subtractCents(netYieldPhase1, input.irregularReductionAmount)
        : netYieldPhase1;

    return {
        difference,
        difficultJustificationExpense,
        netYieldPhase1,
        netYieldReduced,
        // Fase 3ª (TRADE/12.000€/inicio actividad/artísticas): ninguna aplica en caso-07; sin
        // campos de entrada para ellas todavía (GAPS de Cap.06/07 — ver docs/guia-renta-2025).
        netYieldReducedTotal: netYieldReduced,
    };
}

// ============================================================================================
// T4.3 — Estimación objetiva NO agrícola (Cap.08 §3) — solo epígrafe 673.1 (caso-08)
// ============================================================================================

export interface ObjectiveEstimationModuleUnit {
    moduleId: string;
    units: number;
    yieldPerUnit: Cents;
}

interface EmploymentBracketsParam {
    // Coeficiente por tramos (marginal, como una escala) sobre las unidades restantes tras el incremento.
    brackets: { upTo: number | null; rate: number }[];
}

interface ExcessCorrectorParam {
    multiplier: number; // P-NEW-11-14, 1,30
}

interface Epigraph673_1Param {
    excessThreshold: Cents; // cuantía a partir de la cual se aplica el índice de exceso
    modules: Record<string, Cents>; // rendimiento anual por unidad, por moduleId
}

/**
 * Unidades de módulo con 2 decimales — Cap.08 §3.3 "resultado con 2 decimales". El golden de
 * caso-08 trunca (2×600/1.800=0,66, no 0,67 por redondeo estándar); se sigue esa convención
 * (verificada también en las cifras que dependen de ella: 3,66 en todas las filas posteriores).
 */
function truncateModuleUnits(value: number): number {
    return Math.floor(value * 100) / 100;
}

/** Cap.08 §3.2 (P-NEW-11-2/P-NEW-11-3) — minoración por incentivos al empleo (personal asalariado). */
export function computeEmploymentIncentiveDeduction(
    currentYearUnits: number,
    previousYearUnits: number,
    salariedYieldPerUnit: Cents,
    params: ParamRegistry
): Cents {
    const incrementCoefficient = params.get<number>('P-NEW-11-2');
    const { brackets } = params.get<EmploymentBracketsParam>('P-NEW-11-3');
    const increment = Math.max(0, currentYearUnits - previousYearUnits);
    const incrementCoef = incrementCoefficient * increment;

    const remainingUnits = currentYearUnits - increment;
    let bracketCoef = 0;
    let lowerBound = 0;
    for (const bracket of brackets) {
        const upTo = bracket.upTo ?? Infinity;
        if (remainingUnits <= lowerBound) break;
        const portion = Math.min(remainingUnits, upTo) - lowerBound;
        if (portion > 0) bracketCoef += portion * bracket.rate;
        lowerBound = upTo;
    }

    const totalCoefficient = incrementCoef + bracketCoef;
    return roundHalfUp(totalCoefficient * salariedYieldPerUnit);
}

/** Cap.08 §3, Fase 3ª (P-NEW-11-14) — índice corrector de exceso. */
export function applyExcessCorrector(netMinoredYield: Cents, excessThreshold: Cents, params: ParamRegistry): Cents {
    if (netMinoredYield <= excessThreshold) return netMinoredYield;
    const { multiplier } = params.get<ExcessCorrectorParam>('P-NEW-11-14');
    const excess = subtractCents(netMinoredYield, excessThreshold);
    return sumCents([excessThreshold, roundHalfUp(excess * multiplier)]);
}

export interface ObjectiveEstimationNonAgriculturalInput {
    iaeCode: '673.1';
    /** Las unidades de "PERSONAL_ASALARIADO" (si presente) alimentan también la minoración de Fase 2ª. */
    modules: { moduleId: string; units: number }[];
    salariedUnitsPreviousYear: number;
    investmentIncentiveDeduction: Cents;
    extraordinaryExpenses: Cents;
}

export interface ObjectiveEstimationResult {
    netYieldPrevious: Cents;
    employmentIncentiveDeduction: Cents;
    investmentIncentiveDeduction: Cents;
    netYieldReduced: Cents; // "minorado"
    netYieldModules: Cents;
    generalReduction: Cents;
    netYieldActivity: Cents;
    netYieldReducedTotal: Cents;
}

const SUPPORTED_NON_AGRICULTURAL_EPIGRAPHS = ['673.1'] as const;

/** Cap.08 §3 — pipeline EO no agrícola, 5 fases. Solo epígrafe 673.1 (caso-08). */
export function computeObjectiveEstimationNonAgricultural(
    input: ObjectiveEstimationNonAgriculturalInput,
    params: ParamRegistry
): ObjectiveEstimationResult {
    if (!SUPPORTED_NON_AGRICULTURAL_EPIGRAPHS.includes(input.iaeCode)) {
        throw new Error(
            `Epígrafe IAE "${input.iaeCode}" sin tabla de módulos implementada. Solo ${SUPPORTED_NON_AGRICULTURAL_EPIGRAPHS.join(', ')} ` +
            '(regla dura §F4: no aproximar epígrafes sin tabla confirmada).'
        );
    }
    const epigraph = params.get<Epigraph673_1Param>('P-EPIGRAFE-673.1');

    // Fase 1ª
    const moduleYields = input.modules.map((m) => {
        const rate = epigraph.modules[m.moduleId];
        if (rate === undefined) {
            throw new Error(`Módulo desconocido "${m.moduleId}" para epígrafe ${input.iaeCode}.`);
        }
        return roundHalfUp(truncateModuleUnits(m.units) * rate);
    });
    const netYieldPrevious = sumCents(moduleYields);

    // Fase 2ª
    const salariedModule = input.modules.find((m) => m.moduleId === 'PERSONAL_ASALARIADO');
    const salariedRate = epigraph.modules['PERSONAL_ASALARIADO'];
    const employmentIncentiveDeduction = salariedModule
        ? computeEmploymentIncentiveDeduction(
              truncateModuleUnits(salariedModule.units),
              input.salariedUnitsPreviousYear,
              salariedRate,
              params
          )
        : toCents(0);
    const netYieldReduced = subtractCents(
        netYieldPrevious,
        sumCents([employmentIncentiveDeduction, input.investmentIncentiveDeduction])
    );

    // Fase 3ª — solo si RN minorado > 0 (Cap.08 §3, caso límite "RN minorado negativo")
    const netYieldModules = netYieldReduced > 0 ? applyExcessCorrector(netYieldReduced, epigraph.excessThreshold, params) : netYieldReduced;

    // Fase 4ª
    const generalReductionPct = params.get<number>('P-073');
    const generalReduction = roundHalfUp(netYieldModules * (generalReductionPct / 100));
    const netYieldActivity = subtractCents(netYieldModules, sumCents([generalReduction, input.extraordinaryExpenses]));

    // Fase 5ª — sin irregularidad ni reducción <12.000€ en caso-08 (RN > 12.000€)
    return {
        netYieldPrevious,
        employmentIncentiveDeduction,
        investmentIncentiveDeduction: input.investmentIncentiveDeduction,
        netYieldReduced,
        netYieldModules,
        generalReduction,
        netYieldActivity,
        netYieldReducedTotal: netYieldActivity,
    };
}

// ============================================================================================
// T4.4 — Estimación objetiva agrícola/ganadera (Cap.09 §3) — solo códigos 3/6/15 (caso-09)
// ============================================================================================

export interface AgriculturalProductIncome {
    productCode: string;
    /** Ingresos ya incluida la parte proporcional de la ayuda PAC, si aplica. */
    computableIncome: Cents;
}

interface AgriculturalIndexTableParam {
    byProductCode: Record<string, number>;
}

interface SalariedCostCorrectorParam {
    // Tramos EXPLÍCITOS con mínimo y máximo: la tabla de Cap.09 §4 (P-NEW-12-11) solo define
    // tramos desde el 10 % (10-20/20-30/30-40/>40 %); por debajo de 10 % no hay corrector citado
    // (se devuelve 1, sin corrección — ver `computeSalariedCostCorrectorIndex`).
    brackets: { minPct: number; maxPct: number | null; index: number }[];
}

export interface ObjectiveEstimationAgriculturalInput {
    products: AgriculturalProductIncome[];
    amortization: Cents;
    salariedPersonnelCost: Cents;
    totalIncomeVolume: Cents; // para el índice corrector de personal asalariado (coste/ingresos)
}

/** Cap.09 §4 (P-NEW-12-1x tabla de códigos) — índice de rendimiento neto por producto/servicio. */
export function computeAgriculturalNetYieldPrevious(products: AgriculturalProductIncome[], params: ParamRegistry): Cents {
    const table = params.get<AgriculturalIndexTableParam>('P-AGRI-PRODUCT-INDEX');
    const perProduct = products.map((p) => {
        const index = table.byProductCode[p.productCode];
        if (index === undefined) {
            throw new Error(`Código de producto "${p.productCode}" sin índice de rendimiento neto implementado.`);
        }
        return roundHalfUp(p.computableIncome * index);
    });
    return sumCents(perProduct);
}

/** Cap.09 §4 (P-NEW-12-11) — índice corrector "personal asalariado" por tramos de coste/ingresos. */
export function computeSalariedCostCorrectorIndex(salariedCost: Cents, totalIncomeVolume: Cents, params: ParamRegistry): number {
    const { brackets } = params.get<SalariedCostCorrectorParam>('P-NEW-12-11');
    const ratioPct = (salariedCost / totalIncomeVolume) * 100;
    const bracket = brackets.find((b) => ratioPct >= b.minPct && (b.maxPct === null || ratioPct <= b.maxPct));
    return bracket ? bracket.index : 1; // <10% (o fuera de tabla) ⇒ sin corrector
}

export function computeObjectiveEstimationAgricultural(
    input: ObjectiveEstimationAgriculturalInput,
    params: ParamRegistry
): ObjectiveEstimationResult {
    // Fase 1ª
    const netYieldPrevious = computeAgriculturalNetYieldPrevious(input.products, params);

    // Fase 2ª
    const netYieldReduced = subtractCents(netYieldPrevious, input.amortization);

    // Fase 3ª
    const correctorIndex = computeSalariedCostCorrectorIndex(input.salariedPersonnelCost, input.totalIncomeVolume, params);
    const netYieldModules = roundHalfUp(netYieldReduced * correctorIndex);

    // Fase 4ª
    const generalReductionPct = params.get<number>('P-073');
    const generalReduction = roundHalfUp(netYieldModules * (generalReductionPct / 100));
    const netYieldActivity = subtractCents(netYieldModules, generalReduction);

    // Fase 5ª — sin irregularidad ni reducción <12.000€ en caso-09 (RN > 12.000€)
    return {
        netYieldPrevious,
        employmentIncentiveDeduction: toCents(0),
        investmentIncentiveDeduction: input.amortization,
        netYieldReduced,
        netYieldModules,
        generalReduction,
        netYieldActivity,
        netYieldReducedTotal: netYieldActivity,
    };
}

// ============================================================================================
// T4.5 — Incompatibilidades (Cap.06 §3.5, Cap.07 §2)
// ============================================================================================

export type ActivityMethod = 'ED_NORMAL' | 'ED_SIMPLIFIED' | 'EO';

/** Una actividad en EDS/EDN obliga a todas las demás al mismo método (Cap.07 §2, "incompatibilidad rígida"). */
export function assertMethodConsistency(methods: ActivityMethod[]): void {
    const directMethods = new Set(methods.filter((m) => m !== 'EO'));
    if (directMethods.size > 1) {
        throw new Error(
            `Incompatibilidad de métodos: no pueden coexistir ${[...directMethods].join(' y ')} en el mismo ejercicio ` +
            '(una actividad en EDN o EDS obliga a todas — Cap.07 §2).'
        );
    }
    const hasDirect = directMethods.size > 0;
    const hasObjective = methods.includes('EO');
    if (hasDirect && hasObjective) {
        throw new Error('Incompatibilidad de métodos: una actividad en estimación directa excluye EO para todas las demás (Cap.07 §2).');
    }
}

/** Umbral EDS (P-060): cifra de negocios del año anterior > 600.000 € obliga a EDN. */
export function assertEdsEligible(previousYearRevenue: Cents, params: ParamRegistry): void {
    const threshold = params.get<Cents>('P-060');
    if (previousYearRevenue > threshold) {
        throw new Error(`Cifra de negocios del año anterior (${previousYearRevenue} cts) supera el umbral EDS (P-060); obliga a EDN.`);
    }
}
